import { useNavigate, useParams } from "react-router-dom";
import {
    BundleTypes,
    fetchCollectionProducts,
    fetchStoreProducts,
    updateBundleConfigWithRealTimeProducts,
    generateRandomUniqueId,
    generateSwatchConfigsFromProductMap,
} from "../constants";
import { FormProvider, useWatch } from "react-hook-form";
import { VolumeFormPreviewWrapper } from "./VolumeFormPreviewWrapper/VolumeFormPreviewWrapper";
import { useBundleEditor, SKIP_KEYS } from "./hooks/useBundleEditor";
import { VariantTabs } from "../../../components/VariantEditor/VariantTabs";
import { VariantFieldContext } from "../../../components/FormFields";
import VolumeContentForm from "./VolumeContentForm/VolumeContentForm";
import VolumeDesignForm from "./VolumeDesignForm/VolumeDesignForm";
import VolumeTargetForm from "./VolumeTargetForm/VolumeTargetForm";
import { initialConfigData } from "./hooks/useVolumeHook";
import styles from "./Volume.module.css";
import { endPoints, getData, postData } from "../../../utilities/networkCalls";
import { createContext, Dispatch, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@shopify/polaris";
import { getBundleRoute } from "../../constants";
import { VolumePreview } from "./VolumeBundle/VolumeBundle";
import { VolumeConfigInterface, Action, ProductObj } from "../Types";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getUpdatedTargeting } from "../../shouldRenderTargeting";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { scrollToTop, getBoolean } from "../../../utilities/functions";
import { deepMergeWithDefaults } from "../../utilities";
import { useInspectorMode } from "../../../hooks/useInspectorMode";
import VolumeTemplateSelectionScreen from "./VolumeTemplateSelectionScreen/VolumeTemplateSelectionScreen";
import { isAbTestingEnabledPlan } from "../../../components/Plans/plansConstants";
import StopABTestModal from "../../ABTest/StopABTestModal";

export const MAX_QTY = 100;

export interface VolumeBundleContextType {
    dispatchBundleConfig: Dispatch<Action<VolumeConfigInterface>>;
    handleNext: (step: number, stepName: string) => void;
    isMobile: boolean;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    inspectorMode?: boolean;
    setInspectorMode?: Dispatch<React.SetStateAction<boolean>>;
    inspectorTarget: any | null;
    openDefaultVariantSelector: boolean;
    setOpenDefaultVariantSelector: Dispatch<React.SetStateAction<boolean>>;
    activeQuantity: number;
    setActiveQuantity: Dispatch<React.SetStateAction<number>>;
    activeProduct: ProductObj | null;
    setActiveProduct: Dispatch<React.SetStateAction<ProductObj | null>>;
    selectedTab: number;
    setSelectedTab: Dispatch<React.SetStateAction<number>>;
}

export const VolumeBundleContext = createContext<VolumeBundleContextType>(null as any);

const VolumeDiscount = ({
    forAiBundle = false,
    preConfig = "",
    setPreConfig = () => {},
    setFormState = () => {},
    setSavingBundle = () => {},
}: {
    preConfig?: string;
    forAiBundle?: boolean;
    formPreviewWrapperRef?: any;
    onlyPreview?: boolean;
    setPreConfig?: Dispatch<any>;
    setFormState?: any;
    setSavingBundle?: any;
}) => {
    const appConfigContext = useContext(AppConfigContext);
    const { sft, globalSettingsInfo, themeColorsInfo } = appConfigContext;
    const { globalSettings, loading } = globalSettingsInfo;
    const { trackFromTabs, trackCtaClick } = useBundleMixpanelHook();
    const SwatchesBundleConfigTouchedRef = useRef<Record<string, string>>({});
    const { mergeDesignWithThemeColors } = themeColorsInfo;
    const userSaved = globalSettingsInfo?.globalSettings?.design_settings_volume?.user_saved;

    // ─────────────────────────────────────────────────────────────────────────
    // Bundle Editor V2 — single RHF instance, variant cache, A/B logic
    // ─────────────────────────────────────────────────────────────────────────
    const editor = useBundleEditor({
        initialValues: initialConfigData,
        onSave: async () => {},
    });

    // Reactive watches — only subscribe to the fields actually needed for
    // conditional rendering / side-effects in this component.
    const formId = useWatch({ control: editor.form.control, name: "form_id" });
    const selectedTemplateVolume = useWatch({ control: editor.form.control, name: "selected_template_volume" });
    const volumeProductSelection = useWatch({ control: editor.form.control, name: "volume_product_selection" });
    const collections = useWatch({ control: editor.form.control, name: "collections" }) ?? [];
    const products = useWatch({ control: editor.form.control, name: "products" }) ?? [];
    // Full form watch — drives live preview so every field change is reflected immediately
    const liveFormValues = useWatch({ control: editor.form.control }) as any as VolumeConfigInterface;

    // Bridge: keep dispatchBundleConfig available for downstream components
    // that haven't been migrated yet.  It writes directly into the RHF store.
    const dispatchBundleConfig = useCallback(
        (action: Action<VolumeConfigInterface>) => {
            if (action?.key) {
                editor.form.setValue(action.key as any, action.payload, { shouldDirty: true });
            } else if (action?.init) {
                const initVal = action.init as VolumeConfigInterface;
                Object.entries(initVal).forEach(([key, val]) => {
                    editor.form.setValue(key as any, val, { shouldDirty: true });
                });
            }
        },
        [editor.form],
    );

    const [selected, setSelected] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [fetchingBundle, setFetchingBundle] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const { bId } = useParams();
    const goTo = useNavigate();

    // Expose isDirty to parent (forAiBundle usage)
    useEffect(() => {
        setFormState && setFormState({ dirty: editor.form.formState.isDirty });
    }, [editor.form.formState.isDirty, setFormState]);

    useEffect(() => {
        if (!fetchingBundle) {
            setPreConfig && setPreConfig({ ...editor.form.getValues() });
            setSavingBundle && setSavingBundle(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingBundle]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            if (!forAiBundle && bId) {
                fetchBundle(bId);
            } else if (forAiBundle && preConfig) {
                fetchBundle(preConfig);
            } else {
                setFetchingBundle(false);

                const currentValues = editor.form.getValues();
                // const mergedDesignSettings = mergeDesignWithThemeColors(globalSettings?.design_settings_volume);

                const newConfig = {
                    bundles_design: {
                        ...currentValues?.bundles_design,
                        // ...(userSaved ? globalSettings?.design_settings_volume : mergedDesignSettings),
                    } as VolumeConfigInterface["bundles_design"],
                    general_settings: {
                        ...currentValues?.general_settings,
                        ...(globalSettings?.hasOwnProperty("general_settings")
                            ? {
                                  ...globalSettings?.general_settings,
                                  show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                      "show_powered_by_kefi",
                                  )
                                      ? globalSettings?.general_settings?.show_powered_by_kefi
                                      : true,
                              }
                            : {}),
                    },
                    bundles_settings: {
                        ...currentValues?.bundles_settings,
                        ...globalSettings?.bundle_settings_volume,
                    } as VolumeConfigInterface["bundles_settings"],
                };

                editor.form.reset({ ...currentValues, ...newConfig });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalSettings, loading, bId]);

    useEffect(() => {
        if (formId && !forAiBundle) {
            goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT, formId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId, forAiBundle]);

    const fetchBundle = async (id: string | null = "") => {
        if (!id) {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT));
        }
        setFetchingBundle(true);
        const res = await getData({ url: endPoints.getBundle + id });

        if (res.status == 200) {
            if (res?.data?.bundle?.form_id) {
                const newConfig = await updateBundleConfigWithRealTimeProducts(sft, [res.data.bundle]);
                const currentValues = editor.form.getValues();
                const responseDesign = res.data.bundle?.bundles_design;
                const _c = {
                    ...deepMergeWithDefaults(currentValues, res.data.bundle),
                    volume_discounts: res.data.bundle.volume_discounts.map((incoming: any) => {
                        const existing = currentValues.volume_discounts?.[0];
                        return { ...existing, ...incoming };
                    }),
                    products: newConfig[0].products,
                    publish_count: res.data.publish_count,
                };
                _c.bundles_design = {
                    ...currentValues?.bundles_design,
                    ...(userSaved ? _c.bundles_design : mergeDesignWithThemeColors(_c.bundles_design)),
                    free_gift_compare_price_color:
                        responseDesign?.free_gift_compare_price_color ?? responseDesign?.product_price_color,
                    free_gift_compare_price_size:
                        responseDesign?.free_gift_compare_price_size ?? responseDesign?.product_price_size,
                    free_gift_compare_price_style:
                        responseDesign?.free_gift_compare_price_style ?? responseDesign?.product_price_style,
                    add_to_cart_hover_bg_color:
                        responseDesign?.add_to_cart_hover_bg_color ?? responseDesign?.add_to_cart_bg_color,
                    bundle_border_radius: responseDesign?.bundle_border_radius ?? responseDesign?.border_radius,
                };
                _c.general_settings = {
                    ...currentValues?.general_settings,
                    ..._c.general_settings,
                    show_powered_by_kefi: _c?.general_settings?.hasOwnProperty("show_powered_by_kefi")
                        ? _c?.general_settings?.show_powered_by_kefi
                        : true,
                } as unknown as VolumeConfigInterface["general_settings"];
                _c.targeting = { ..._c.targeting, ...getUpdatedTargeting(_c.targeting) };

                // initializeEditor rebuilds the full variant cache from ab_config
                // (for A/B bundles) OR just sets the base (for regular bundles).
                editor.initializeEditor(_c);
                setSelectedTab(0);
                setFetchingBundle(false);
            } else {
                shopify.saveBar.hide("save-bar");
                if (!forAiBundle) {
                    goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT));
                }
            }
        } else {
            shopify.saveBar.hide("save-bar");
            if (!forAiBundle) {
                goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT));
            }
        }
    };

    // Real-time-refreshes product stock/availability before handing the
    // "set winner" bundle off to the editor, so it doesn't show stale
    // out-of-stock/price data — same reason fetchBundle does this.
    //
    // markAbTestWinner only returns the bundle's `settings` sub-document, so
    // it can't include fields that live on the parent doc (status, shop,
    // bundle_embed_id, user_id) — patch those back in from the current form
    // before replacing, or Publish/Unpublish and the live-bundle check break.
    const handleWinnerApplied = async (bundle: any) => {
        // `shop`/`bundle_embed_id`/`user_id` live on the bundle doc but aren't
        // part of VolumeConfigInterface (RHF's type), so they're read via `any`.
        const currentValues = editor.form.getValues() as any;
        const [enriched] = await updateBundleConfigWithRealTimeProducts(sft, [bundle]);
        editor.applyWinnerBundle({
            ...currentValues,
            ...enriched,
            volume_discounts: (enriched as VolumeConfigInterface)?.volume_discounts?.map((incoming: any) => ({
                ...currentValues.volume_discounts?.[0],
                ...incoming,
            })),
            status: currentValues.status,
            shop: currentValues.shop,
            bundle_embed_id: currentValues.bundle_embed_id,
            user_id: currentValues.user_id,
        } as any as VolumeConfigInterface);
    };

    const handleNext = async (num: number, screen = "-") => {
        const valid = await editor.form.trigger();
        if (valid) {
            trackFromTabs({
                cta: "Form Tabs Switched",
                tabName: screen,
                bundleType: editor.form.getValues("bundle_type"),
            });
            setSelected(num);
        } else {
            scrollToTop();
        }
    };

    const [currentActiveProduct, setCurrentActiveProduct] = useState<string>(products[0]?.id || "");
    const [collectionProducts, setCollectionProducts] = useState<ProductObj[]>([]);
    const [loadingCollectionProducts, setLoadingCollectionProducts] = useState(false);
    const [allStoreProducts, setAllStoreProducts] = useState<ProductObj[]>([]);
    const [loadingAllStoreProducts, setLoadingAllStoreProducts] = useState(false);

    useEffect(() => {
        const fetchProducts = async () => {
            if (volumeProductSelection === "collection" && collections?.length > 0) {
                setLoadingCollectionProducts(true);
                try {
                    const allProducts: ProductObj[] = [];
                    for (const collection of collections) {
                        const collectionId = collection.id.split("/").pop();
                        if (collectionId) {
                            const { products: collProducts } = await fetchCollectionProducts(
                                collectionId,
                                sft,
                                () => {},
                                false,
                                20,
                            );
                            allProducts.push(...collProducts);
                        }
                    }
                    const uniqueProducts = Array.from(new Map(allProducts.map((p) => [p.id, p])).values());
                    setCollectionProducts(uniqueProducts);

                    if (uniqueProducts.length > 0) {
                        setCurrentActiveProduct(uniqueProducts[0].id);
                    }
                } catch (error) {
                    console._log("Error fetching collection products", error);
                } finally {
                    setLoadingCollectionProducts(false);
                }
            } else {
                setCollectionProducts([]);
            }
        };

        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collections, volumeProductSelection, sft]);

    useEffect(() => {
        const fetchAllProducts = async () => {
            if (volumeProductSelection === "all" || volumeProductSelection === "exclude") {
                setLoadingAllStoreProducts(true);
                try {
                    const storeProducts = await fetchStoreProducts(sft, false, 50);
                    setAllStoreProducts((storeProducts || []) as ProductObj[]);
                } catch (error) {
                    console._log("Error fetching store products", error);
                } finally {
                    setLoadingAllStoreProducts(false);
                }
            } else {
                setAllStoreProducts([]);
            }
        };

        fetchAllProducts();
    }, [volumeProductSelection, sft]);

    const previewableProducts: ProductObj[] = useMemo(() => {
        if (volumeProductSelection === "collection") return collectionProducts;
        if (volumeProductSelection === "all") return allStoreProducts;
        if (volumeProductSelection === "exclude") {
            return allStoreProducts.filter(
                (sp) => !(products as ProductObj[])?.some((excluded) => excluded.id === sp.id),
            );
        }
        return [];
    }, [volumeProductSelection, collectionProducts, allStoreProducts, products]);
    const loadingPreviewableProducts =
        volumeProductSelection === "collection" ? loadingCollectionProducts : loadingAllStoreProducts;

    useEffect(() => {
        if (volumeProductSelection === "specific") {
            setCurrentActiveProduct(products?.[0]?.id || "");
        } else if (volumeProductSelection === "all" || volumeProductSelection === "exclude") {
            setCurrentActiveProduct((prev) =>
                previewableProducts.some((p) => p.id === prev) ? prev : previewableProducts[0]?.id || "",
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, volumeProductSelection, previewableProducts]);

    // --- Inspector mode -----
    const [inspectorMode, setInspectorMode] = useState(false);
    const hoveredElRef = useRef<HTMLElement | null>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const { handleInspectorClick } = useInspectorMode({
        inspectorMode,
        previewRef,
        selectedEl,
        setSelectedEl,
        hoveredElRef,
        setInspectorTarget,
        handleNext,
        // Pass a live getter so the inspector always sees the latest form values
        bundleConfig: editor.form.getValues() as any,
    });

    const [openDefaultVariantSelector, setOpenDefaultVariantSelector] = useState(false);
    const [activeQuantity, setActiveQuantity] = useState(0);
    const [activeProduct, setActiveProduct] = useState<ProductObj | null>(null);

    useEffect(() => {
        const productList = (volumeProductSelection === "specific" ? products : previewableProducts) as ProductObj[];
        const p = productList?.filter((p: ProductObj) => p.id == currentActiveProduct);
        if (p?.length > 0) {
            setActiveProduct(p[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeProductSelection, currentActiveProduct]);

    // VolumeFormPreviewWrapper's "Preview on Store"/"Show on Store" buttons read
    // localStorage("qb_handle") to build the preview URL for non-"specific"
    useEffect(() => {
        if (volumeProductSelection === "specific" || !activeProduct) return;
        window.qb_product = activeProduct;
        window.localStorage.setItem("qb_handle", activeProduct.handle);
    }, [volumeProductSelection, activeProduct]);

    // Plan info
    const { plansInfo } = appConfigContext;
    const { isExhausted, isTrial, currentPlan } = plansInfo;
    const abTestingLocked = !isAbTestingEnabledPlan(currentPlan?.plan_id);
    const [activatingBundle, setActivatingBundle] = useState(false);
    const [savingBundleLocal, setSavingBundleLocal] = useState(false);
    const [stopAbModalOpen, setStopAbModalOpen] = useState(false);

    const lockedTooltipContent = abTestingLocked ? (
        <span>
            Available on a higher plan.{" "}
            <Link
                onClick={() => {
                    if (editor.form.formState.isDirty || editor.hasUnsavedChanges) {
                        shopify.saveBar.leaveConfirmation();
                        return;
                    }
                    goTo("/billing");
                }}
            >
                Upgrade Plan
            </Link>
        </span>
    ) : (
        ""
    );

    // Stopping an AB setup that was never saved is purely local (no experiment
    // exists server-side yet). Stopping a persisted one requires picking a
    // winner — see StopABTestModal, which mirrors ABTestDashboard's flow.
    const onStopAB = () => {
        if (abTestingLocked) return;
        if (!editor.abEnabled) return;
        trackCtaClick({ cta: "Stop A/B Testing", ctaType: "Click", bundleType: BundleTypes.VOLUME_DISCOUNT });
        if (editor.savedAbEnabled) {
            setStopAbModalOpen(true);
        } else {
            editor.disableAB();
        }
    };

    const onEnableAB = () => {
        if (abTestingLocked) return;
        trackCtaClick({ cta: "Enable A/B Testing", ctaType: "Click", bundleType: BundleTypes.VOLUME_DISCOUNT });
        editor.enableAB();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Save — RHF validates, builds payload, calls API
    // ─────────────────────────────────────────────────────────────────────────
    const handleSaveBundle = async () => {
        const valid = await editor.form.trigger();
        if (!valid) {
            scrollToTop();
            return;
        }

        setSavingBundleLocal(true);
        const rhfValues = editor.form.getValues() as any;

        if (!rhfValues?.form_id) {
            setInspectorMode(false);
        }

        const normalizeVolumeDiscounts = (vd: any[] | undefined) =>
            vd?.map((v: any) => ({
                ...v,
                discount_value: parseFloat(String(v.discount_value)) || 0,
            })) ?? vd;

        let payload: VolumeConfigInterface;

        if (editor.abEnabled && editor.variants.length > 0) {
            // IMPORTANT: when AB testing is active, `rhfValues` is the
            // currently-ACTIVE VARIANT's fully-merged doc (base + that
            // variant's overrides), not the hidden base document. Using it
            // for the top-level payload fields would leak whatever the
            // active variant currently shows (e.g. an edited volume_discounts)
            // into the base itself. `editor.buildPayload()` already computes
            // the correct hidden base + properly-diffed per-variant overrides
            // — use it wholesale instead of cherry-picking just `ab_config`.
            const abPayload = editor.buildPayload() as any;
            payload = {
                ...abPayload,
                volume_discounts: normalizeVolumeDiscounts(abPayload.volume_discounts),
                ab_config: {
                    ...abPayload.ab_config,
                    variants: abPayload.ab_config.variants.map((v: any) => ({
                        ...v,
                        overrides: v.overrides?.volume_discounts
                            ? {
                                  ...v.overrides,
                                  volume_discounts: normalizeVolumeDiscounts(v.overrides.volume_discounts),
                              }
                            : v.overrides,
                    })),
                },
            };
        } else {
            payload = {
                ...rhfValues,
                volume_discounts: normalizeVolumeDiscounts(rhfValues.volume_discounts),
            };
            delete (payload as any).ab_config;
        }

        const res = await postData({
            url: payload?.form_id ? endPoints.updateBundle : endPoints.saveBundle,
            payload: { ...payload },
        });

        if (res.status === 200 || res.status === 201) {
            // appConfigContext.globalSettingsInfo.globalSettings.design_settings_volume = {
            //     ...payload.bundles_design,
            //     user_saved: true,
            // };

            if (!rhfValues?.form_id) {
                editor.form.setValue("form_id", res.data.data, { shouldDirty: false });
            }

            shopify.saveBar.hide("bundle-save-bar");

            if (!forAiBundle) {
                goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT, res.data.data || rhfValues?.form_id));
            }

            shopify.toast.show("Data saved successfully.", { isError: false, duration: 1500 });

            // Swatch metafield sync stays out-of-band, same as other bundle
            // types — synced after save completes, independent of the AB
            // variant cache. Reads from `payload` (the base doc), not the
            // active variant's merged view.
            const changedSwatches = generateSwatchConfigsFromProductMap((payload as any)?.swatchesProductsMap || {});
            if (changedSwatches.length > 0) {
                const grouped: Record<string, any[]> = {};
                changedSwatches.forEach((item) => {
                    if (!grouped[item.productId]) grouped[item.productId] = [];
                    grouped[item.productId].push(item);
                });
                await postData({
                    url: endPoints.updateSwatchesMeta,
                    payload: { kefi_swatches: Object.values(grouped).map((group) => ({ options: group })) },
                });
            }

            // Refreshes the saved configuration baseline in useBundleEditor.
            // Must be the actual base+ab_config document that was just
            // persisted (`payload`), NOT `editor.form.getValues()` — the
            // latter is whatever variant tab is currently active (base +
            // that variant's overrides already merged in), and feeding that
            // back in as "the base" is what caused the active variant's
            // edits to leak into the base document on the next save.
            editor.updateLastSavedConfig({
                ...payload,
                form_id: res.data.data || payload.form_id,
            } as any);
        } else {
            shopify.toast.show("Failed to save data. Please try again.", { isError: true, duration: 2000 });
        }

        setSavingBundle(false);
        setSavingBundleLocal(false);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Discard — reset RHF to last saved config
    // ─────────────────────────────────────────────────────────────────────────
    const handleDiscardBundle = () => {
        editor.resetCache();
        shopify.saveBar.hide("bundle-save-bar");
        setSelected(0);
        setSelectedTab(0);
    };

    const handleActivate = async (currentStatus: boolean) => {
        if (!isTrial && isExhausted && !currentStatus) return;
        const currentFormId = editor.form.getValues("form_id");
        if (!currentFormId) return;
        setActivatingBundle(true);
        try {
            if (!currentStatus) {
                const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
                if (isExtensionEnabled.data.status === 0) return;
            }
            const res = await postData({
                url: endPoints.updateBundleStatus,
                payload: { form_id: currentFormId, status: !currentStatus, delete: false },
            });
            if (res.status === 200 || res.status === 201) {
                editor.patchAllTargets("status", !currentStatus);
                shopify.toast.show("Bundle status updated successfully!", { isError: false });
            } else {
                shopify.toast.show("Failed to update bundle status. Please try again.", { isError: true });
            }
        } catch {
            shopify.toast.show("An error occurred while updating bundle status.", { isError: true });
        } finally {
            setActivatingBundle(false);
        }
    };

    const handleDuplicate = async (name: string) => {
        if (!name) return;
        // Same base-vs-active-variant hazard as handleSaveBundle: while AB
        // testing is active, editor.form.getValues() is the active variant's
        // merged view, not the hidden base — using it directly would bake
        // that variant's values in as the duplicate's base. Go through
        // buildPayload() for the correct base, and strip ab_config entirely
        // (a duplicate is a new, independent bundle — it must not inherit the
        // original's exp_id, or visitor/click tracking and A/B stats would
        // get attributed across both bundles).
        const currentValues =
            editor.abEnabled && editor.variants.length > 0
                ? (editor.buildPayload() as any)
                : (editor.form.getValues() as any);
        const duplicatePayload = { ...currentValues };
        delete duplicatePayload.ab_config;
        const res = await postData({
            url: endPoints.saveBundle,
            payload: {
                ...duplicatePayload,
                title: name,
                offer_name: "",
                bundle_embed_id: generateRandomUniqueId(),
            },
        });
        if (res.status === 200) {
            shopify.toast.show("Bundle duplicated successfully", { isError: false });
            if (res?.data?.data) goTo(getBundleRoute(BundleTypes.VOLUME_DISCOUNT, res.data.data));
        } else {
            shopify.toast.show("Error duplicating bundle", { isError: true });
        }
    };

    // Derive the preview product list directly from live watched values
    const previewProducts = (
        volumeProductSelection === "specific" ? (products as ProductObj[]) : previewableProducts
    )?.filter((p) => p.id == currentActiveProduct);

    // Snapshot helper — only used for non-preview props (save payload built via getValues)
    const getSnapshot = () => editor.form.getValues() as any as VolumeConfigInterface;

    return (
        <VolumeBundleContext.Provider
            value={{
                dispatchBundleConfig,
                handleNext,
                isMobile,
                SwatchesBundleConfigTouchedRef,
                setOpenDefaultVariantSelector,
                openDefaultVariantSelector,
                activeQuantity,
                setActiveQuantity,
                activeProduct,
                setActiveProduct,
                inspectorMode,
                setInspectorMode,
                inspectorTarget,
                selectedTab,
                setSelectedTab,
            }}
        >
            {/* FormProvider: single RHF instance wraps the entire editor tree */}
            <FormProvider {...editor.form}>
                <VariantFieldContext.Provider
                    value={{
                        activeTarget: editor.activeTarget,
                        variants: editor.variants,
                        abEnabled: editor.abEnabled,
                        skipKeys: SKIP_KEYS,
                    }}
                >
                    {loading || fetchingBundle || selectedTemplateVolume || formId ? (
                        <VolumeFormPreviewWrapper
                            bundleConfig={getSnapshot()}
                            loading={fetchingBundle || loading}
                            savingBundle={savingBundleLocal}
                            activatingBundle={activatingBundle}
                            onSave={handleSaveBundle}
                            onDiscard={handleDiscardBundle}
                            onActivate={handleActivate}
                            onDuplicate={handleDuplicate}
                            selected={selected}
                            setSelected={setSelected}
                            handleNext={handleNext}
                            onEnableAB={onEnableAB}
                            onStopAB={onStopAB}
                            abTestingLocked={abTestingLocked}
                            lockedTooltipContent={lockedTooltipContent}
                            renderForms={[
                                <VolumeContentForm key="content" />,
                                <VolumeDesignForm key="design" />,
                                <VolumeTargetForm key="targeting" />,
                                // <VolumeSettingsForm key="settings" />,
                            ]}
                            renderPreview={
                                <div
                                    ref={previewRef}
                                    style={{ userSelect: inspectorMode ? "none" : "text" }}
                                    onClickCapture={handleInspectorClick}
                                    className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                                >
                                    <VolumePreview
                                        bundleConfig={{
                                            ...liveFormValues,
                                            products: previewProducts,
                                            SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
                                        }}
                                        pId={0}
                                        appConfigContext={appConfigContext}
                                        targetForm={null}
                                        setActiveQuantity={setActiveQuantity}
                                    />
                                </div>
                            }
                            variantSwitcher={
                                <VariantTabs
                                    activeTarget={editor.activeTarget}
                                    variants={editor.variants}
                                    abEnabled={editor.abEnabled}
                                    abConfig={(editor.form.getValues() as any)?.ab_config}
                                    onSwitchVariant={async (targetId) => {
                                        // switchVariant already flushes current values to cache
                                        // and resets the form — no second reset needed here
                                        return await editor.switchVariant(targetId);
                                    }}
                                    abTestingLocked={abTestingLocked}
                                    onAddVariant={editor.addVariant}
                                    onRemoveVariant={async (variantId) => {
                                        const hadActive = editor.activeTarget === variantId;
                                        await editor.removeVariant(variantId);
                                        if (hadActive) {
                                            const baseValues = editor.form.getValues();
                                            editor.form.reset(baseValues);
                                        }
                                    }}
                                    onUpdateABConfig={editor.updateABConfig}
                                    validationErrorCount={Object.keys(editor.form.formState.errors).length}
                                    lockedTooltipContent={lockedTooltipContent}
                                    bundleType={BundleTypes.VOLUME_DISCOUNT}
                                />
                            }
                            previewableProducts={previewableProducts}
                            loadingPreviewableProducts={loadingPreviewableProducts}
                            currentActiveProduct={currentActiveProduct}
                            setCurrentActiveProduct={setCurrentActiveProduct}
                            forAiBundle={forAiBundle}
                            inspectorMode={inspectorMode}
                            setInspectorMode={setInspectorMode}
                            hasUnsavedChanges={editor.hasUnsavedChanges}
                        />
                    ) : (
                        <VolumeTemplateSelectionScreen makeClean={() => editor.form.reset(editor.form.getValues())} />
                    )}
                    <StopABTestModal
                        open={stopAbModalOpen}
                        onClose={() => setStopAbModalOpen(false)}
                        formId={getSnapshot().form_id || ""}
                        bundleType="volume"
                        isPublished={!!getBoolean(getSnapshot().status)}
                        onWinnerApplied={handleWinnerApplied}
                    />
                </VariantFieldContext.Provider>
            </FormProvider>
        </VolumeBundleContext.Provider>
    );
};

export default VolumeDiscount;
